/**
 * Browser detection utilities for consistent browser identification across the application
 */

/**
 * Detects if the current browser is Safari (desktop or mobile)
 * @returns {boolean} True if Safari browser is detected
 */
export const isSafari = (): boolean => {
  const userAgent = navigator.userAgent;
  return /Safari/.test(userAgent) && !/Chrome|CriOS/.test(userAgent);
};

/**
 * Detects if the current browser is Android Chrome
 * @returns {boolean} True if Android Chrome browser is detected
 */
export const isAndroidChrome = (): boolean => {
  const userAgent = navigator.userAgent;
  return /Android/.test(userAgent) && /Chrome/.test(userAgent);
};

/**
 * Detects if the current browser is mobile Safari (iOS Safari)
 * @returns {boolean} True if mobile Safari browser is detected
 */
export const isMobileSafari = (): boolean => {
  const userAgent = navigator.userAgent;
  return /Safari/.test(userAgent) && !/Chrome|CriOS/.test(userAgent) && /Mobi/.test(userAgent);
};

/**
 * Detects if the current browser is desktop Safari
 * @returns {boolean} True if desktop Safari browser is detected
 */
export const isDesktopSafari = (): boolean => {
  const userAgent = navigator.userAgent;
  return /Safari/.test(userAgent) && !/Chrome|CriOS/.test(userAgent) && !/Mobi/.test(userAgent);
};

/**
 * Detects if the current browser requires push-to-start behavior for speech recognition
 * (Safari and Android Chrome don't support continuous listening reliably)
 * @returns {boolean} True if the browser requires push-to-start behavior
 */
export const requiresPushToStart = (): boolean => {
  return isSafari() || isAndroidChrome();
};

/**
 * Checks if the browser supports Web Speech API
 * @returns {boolean} True if Web Speech API is supported
 */
export const isSpeechRecognitionSupported = (): boolean => {
  // Safari uses webkitSpeechRecognition exclusively
  return 'webkitSpeechRecognition' in window || 'SpeechRecognition' in window;
};

/**
 * Gets the appropriate restart delay for speech recognition based on browser
 * @returns {number} Delay in milliseconds
 */
const ANDROID_CHROME_RESTART_DELAY = 400;
const SAFARI_RESTART_DELAY = 100;

export const getSpeechRecognitionRestartDelay = (): number => {
  if (isAndroidChrome()) {
    // Android Chrome needs longer delay to prevent rapid restart issues
    return ANDROID_CHROME_RESTART_DELAY;
  }
  if (isSafari()) {
    // Safari needs shorter delay
    return SAFARI_RESTART_DELAY;
  }
  // Other browsers don't need delay
  return 0;
};
